import HeaderSearch from '../components/search/HeaderSearch';
import LocalizationDisclosure from '../components/localization/LocalizationDisclosure';
import Scroll from '../helpers/scroll';
import Splide from '@splidejs/splide';
import { unitlessBreakpoints } from '../helpers';
import { disablePageScroll, enablePageScroll } from '@fluejs/noscroll';

const selectors = {
  pill: '[data-header-pill]',
  headerNavigation: '[data-header-navigation]',
  navHamburger: '[data-nav-hamburger]',
  navClose: '[data-nav-close]',
  megaDetails: '[data-mega-details]',
  submenuBack: '[data-submenu-back]',
  megamenuCarousel: '[data-megamenu-carousel]',
  searchToggle: '[data-header-search-toggle]',
  megamenuGroupToggle: '[data-megamenu-group-toggle]',
  megamenuGroupPanel: '[data-megamenu-group-panel]',
  // Any open nav dropdown, mega or standard — Escape and scroll-down close both.
  openDetails: '.header-navigation details[open]',
};

const classes = {
  pillNavOpen: 'header-pill--nav-open',
  pillScrolled: 'header-pill--scrolled',
  navOpen: 'header-navigation--open',
  hidden: 'hidden',
};

const isDesktop = () => window.innerWidth >= unitlessBreakpoints.lg;

export default class MainHeader {
  constructor(section) {
    this.section = section;
    // One controller for every listener this section owns, so onSectionUnload can
    // drop them all at once instead of pairing up add/remove calls.
    this.controller = new AbortController();
    this.carousels = [];
    this.scrollLocks = new Set();
    this.isNavOpen = false;

    const { settings } = section.data;

    this.pill = this.section.el.querySelector(selectors.pill);
    this.navigation = this.section.el.querySelector(selectors.headerNavigation);
    this.hamburger = this.section.el.querySelector(selectors.navHamburger);
    this.closeButton = this.section.el.querySelector(selectors.navClose);

    if (settings.enable_fixed_header) {
      this.scroll = new Scroll(this.onScroll, this.section.el);
      this.section.el.classList.add(
        'sticky',
        'top-0',
        'z-50',
        'transition-transform',
      );
      this.initScrolledState();
    }
    if (settings.header_transparent) {
      this.section.el.classList.add('transparent-header');
    }

    this.search = new HeaderSearch(this.section.el, this.controller.signal);

    // Search takes over the whole surface at every width, so it is mutually exclusive
    // with the megamenu and with the mobile drawer.
    this.section.el
      .querySelector(selectors.searchToggle)
      ?.addEventListener('click', this.onSearchOpening, {
        signal: this.controller.signal,
      });

    this.initMegamenu();
    this.initMobileNavigation();
    this.initMegamenuCarousel();
    LocalizationDisclosure.init(document.body);

    document.addEventListener('keydown', this.onKeydown, {
      signal: this.controller.signal,
    });
    document.addEventListener('click', this.onDocumentClick, {
      signal: this.controller.signal,
    });
    window.addEventListener('resize', this.onResize, {
      signal: this.controller.signal,
    });

    // Publish the header metrics, then keep them fresh. A one-shot call plus a resize
    // listener is not enough: the logo <img> is emitted with a width but no height, so
    // it cannot be given an aspect ratio and occupies no space at all until it decodes,
    // at which point the header grows by the logo's height. Whether the constructor ran
    // before or after that decode decided the published value, so it alternated with
    // the image cache. ResizeObserver fires once on observe and again on every real
    // size change, which covers the decode, viewport resizes and nav rewrapping alike.
    this.headerResizeObserver = new ResizeObserver(this.publishHeaderMetrics);
    this.headerResizeObserver.observe(this.section.el);
  }

  onSectionUnload = () => {
    this.headerResizeObserver?.disconnect();
    if (this.scrolledFrame) cancelAnimationFrame(this.scrolledFrame);
    this.scroll?.destroy();
    this.carousels.forEach(carousel => carousel.destroy(true));
    this.carousels = [];
    this.controller.abort();
    this.search?.destroy();
    this.releaseScrollLocks();
    if (this.isNavOpen) enablePageScroll();
  };

  // Reopening the containing details makes a selected megamenu block visible in the
  // theme editor instead of silently editing something the merchant cannot see.
  onSectionBlockSelect = ({ el }) => {
    el?.closest('details')?.setAttribute('open', '');
  };

  /*
   * Three numbers, all consumed from CSS:
   * --header-height    the layout space the header occupies; #main-content is pulled
   *                    up by it so the header floats over the first section.
   * --header-clearance the pill's bottom edge, for content that has to sit below it.
   * --header-bottom    the pill's bottom edge in *viewport* coordinates, which is
   *                    where the mobile drawer starts.
   */
  publishHeaderMetrics = () => {
    const root = this.section.el;

    // While hidden on scroll-down the pill is translated out of view, so a
    // measurement taken then would be wrong; the last resting one still holds.
    if (root.classList.contains('-translate-y-full')) return;

    const pill = this.pill || root;
    const occupied = root.offsetHeight;
    const clearance = pill.offsetTop + pill.offsetHeight;

    document.documentElement.style.setProperty(
      '--header-height',
      `${occupied}px`,
    );
    document.documentElement.style.setProperty(
      '--header-clearance',
      `${clearance}px`,
    );
    this.publishHeaderBottom();
  };

  // Split out because it depends on scroll position, which ResizeObserver never sees.
  // Page scroll is locked while the drawer is open, so refreshing it at open time is
  // enough — the pill cannot move underneath it after that.
  publishHeaderBottom = () => {
    const pill = this.pill || this.section.el;
    document.documentElement.style.setProperty(
      '--header-bottom',
      `${Math.max(0, pill.getBoundingClientRect().bottom)}px`,
    );
  };

  /*
   * The Scroll helper is debounced by 100ms, which suits hiding the bar but would
   * leave the background mid-swap while the page is still moving. The pinned state
   * gets its own passive listener instead, coalesced to one class write per frame.
   */
  initScrolledState = () => {
    window.addEventListener('scroll', this.onScrollPosition, {
      passive: true,
      signal: this.controller.signal,
    });
    // The page can load already scrolled — browser scroll restoration, or an anchor.
    this.syncScrolledState();
  };

  onScrollPosition = () => {
    if (this.scrolledFrame) return;

    this.scrolledFrame = requestAnimationFrame(() => {
      this.scrolledFrame = null;
      this.syncScrolledState();
    });
  };

  syncScrolledState = () => {
    this.pill?.classList.toggle(classes.pillScrolled, window.scrollY > 0);
  };

  onScroll = direction => {
    if (this.isNavOpen) return;

    if (direction === 'up') {
      this.section.el.classList.remove('-translate-y-full');
      this.publishHeaderMetrics();
    } else {
      this.closeMegamenus();
      this.section.el.classList.add('-translate-y-full');
    }
  };

  onResize = () => {
    // The drawer markup becomes the desktop bar at lg, so an open drawer has to give
    // up its scroll lock and its open-state styling on the way across.
    if (this.isNavOpen && isDesktop()) this.closeMobileNav();
  };

  onKeydown = event => {
    if (event.key !== 'Escape') return;

    if (this.search?.isOpen) {
      this.search.close();
      this.section.el.querySelector(selectors.searchToggle)?.focus();
      return;
    }

    if (this.isNavOpen) {
      this.closeMobileNav();
      this.hamburger?.focus();
      return;
    }

    const open = this.section.el.querySelector(selectors.openDetails);
    if (open) {
      this.closeMegamenus();
      open.querySelector('summary')?.focus();
    }
  };

  onDocumentClick = event => {
    if (!isDesktop()) return;

    // The search results are inside the pill, so one containment check covers both.
    if (this.pill?.contains(event.target)) return;

    this.closeMegamenus();
    this.search?.close();
  };

  /* ---------------------------------------------------------------- */
  /* Desktop megamenu                                                 */
  /* ---------------------------------------------------------------- */

  initMegamenu = () => {
    this.megaDetails = [
      ...this.section.el.querySelectorAll(selectors.megaDetails),
    ];

    this.megaDetails.forEach(detail => {
      detail.addEventListener('toggle', () => this.syncScrollLock(detail), {
        signal: this.controller.signal,
      });
    });
  };

  /*
   * disablePageScroll/enablePageScroll are reference counted, so every lock must be
   * released exactly once. Tracking which <details> holds a lock — rather than
   * re-testing the viewport on close — keeps that balanced when the panel opens on
   * desktop and is closed after a resize down to mobile, where it takes no lock.
   */
  syncScrollLock = detail => {
    const holdsLock = this.scrollLocks.has(detail);

    if (detail.open) this.search?.close();

    if (detail.open) {
      if (holdsLock || !isDesktop()) return;
      disablePageScroll();
      this.scrollLocks.add(detail);
    } else if (holdsLock) {
      enablePageScroll();
      this.scrollLocks.delete(detail);
    }
  };

  releaseScrollLocks = () => {
    this.scrollLocks.forEach(() => enablePageScroll());
    this.scrollLocks.clear();
  };

  // The search toggle stays reachable while the drawer is open, so stand the drawer
  // down rather than stacking two full-screen surfaces on top of each other.
  onSearchOpening = () => {
    this.closeMegamenus();
    if (this.isNavOpen) this.closeMobileNav();
  };

  // Closing is enough: the toggle handler releases whatever lock the panel took.
  closeMegamenus = () => {
    this.section.el
      .querySelectorAll(selectors.openDetails)
      .forEach(detail => detail.removeAttribute('open'));
  };

  /* ---------------------------------------------------------------- */
  /* Mobile drawer                                                    */
  /* ---------------------------------------------------------------- */

  initMobileNavigation = () => {
    const { signal } = this.controller;

    this.hamburger?.addEventListener('click', this.openMobileNav, { signal });
    this.closeButton?.addEventListener('click', this.closeMobileNav, {
      signal,
    });

    // Back out of a subnav panel by closing the details that owns it.
    this.section.el.querySelectorAll(selectors.submenuBack).forEach(button => {
      button.addEventListener(
        'click',
        () => button.closest('details')?.removeAttribute('open'),
        { signal },
      );
    });

    this.initMegamenuAccordions();
  };

  openMobileNav = () => {
    if (!this.navigation) return;

    this.isNavOpen = true;
    this.pill?.classList.add(classes.pillNavOpen);
    // After the class, not before: the open state lifts the pill flush against the
    // announcement bar, and the drawer hangs off its *new* bottom edge.
    this.publishHeaderBottom();
    this.hamburger?.classList.add(classes.hidden);
    this.hamburger?.setAttribute('aria-expanded', 'true');
    this.closeButton?.classList.remove(classes.hidden);
    this.navigation.classList.add(classes.navOpen);
    disablePageScroll();
    this.closeButton?.focus();
  };

  closeMobileNav = () => {
    if (!this.navigation) return;

    this.isNavOpen = false;
    this.pill?.classList.remove(classes.pillNavOpen);
    this.hamburger?.classList.remove(classes.hidden);
    this.hamburger?.setAttribute('aria-expanded', 'false');
    this.closeButton?.classList.add(classes.hidden);
    this.navigation.classList.remove(classes.navOpen);
    // Leaving a subnav panel open would show it as soon as the drawer reopened.
    this.closeMegamenus();
    enablePageScroll();
  };

  /* ---------------------------------------------------------------- */
  /* Megamenu link groups (accordion below lg, columns at lg)         */
  /* ---------------------------------------------------------------- */

  initMegamenuAccordions = () => {
    this.section.el
      .querySelectorAll(selectors.megamenuGroupToggle)
      .forEach(toggle => {
        toggle.addEventListener(
          'click',
          event => {
            // At lg the groups are columns, not an accordion.
            if (isDesktop()) return;

            const button = event.currentTarget;
            const panel = button.parentElement.querySelector(
              selectors.megamenuGroupPanel,
            );
            if (!panel) return;

            const expanded = button.getAttribute('aria-expanded') === 'true';
            button.setAttribute('aria-expanded', String(!expanded));
            panel.classList.toggle(classes.hidden, expanded);
          },
          { signal: this.controller.signal },
        );
      });
  };

  /* ---------------------------------------------------------------- */
  /* Featured card carousel                                           */
  /* ---------------------------------------------------------------- */

  initMegamenuCarousel = () => {
    this.section.el
      .querySelectorAll(selectors.megamenuCarousel)
      .forEach(carousel => {
        const instance = new Splide(carousel, {
          perPage: 1,
          pagination: false,
          arrows: false,
          gap: '1rem',
          mediaQuery: 'min',
          // Leaves ~296px of a 375px viewport for the first card, as in the design,
          // with the next one peeking in.
          padding: { right: '3rem' },
          breakpoints: {
            [unitlessBreakpoints.sm]: {
              perPage: 2,
              padding: { right: '15%' },
            },
            [unitlessBreakpoints.md]: {
              perPage: 3,
              padding: { right: '10%' },
            },
            [unitlessBreakpoints.lg]: {
              destroy: true,
            },
          },
        });

        instance.mount();
        this.carousels.push(instance);
      });
  };
}
